import type {
  SetupCompletionResult,
  SetupStatus,
  SetupValidationSummary,
} from '../../../types'
import type { SetupValidationCheckStatus } from './types'

export const parseRecordOrNull = (raw: string): Record<string, unknown> | null => {
  try {
    return raw ? (JSON.parse(raw) as Record<string, unknown>) : null
  } catch {
    return null
  }
}

export const readResponsePayload = async (response: Response) => {
  const raw = await response.text()
  const data = parseRecordOrNull(raw)
  return { raw, data }
}

export const toStructuredErrorMessage = (data: Record<string, unknown> | null): string | null => {
  if (typeof data?.message === 'string' && data.message.trim()) return data.message
  const nestedError = data?.error
  if (nestedError && typeof nestedError === 'object' && !Array.isArray(nestedError)) {
    const nestedMessage = (nestedError as { message?: unknown }).message
    if (typeof nestedMessage === 'string' && nestedMessage.trim()) return nestedMessage
  }
  return null
}

export const toResponseError = ({
  raw,
  data,
  fallback,
}: {
  raw: string
  data: Record<string, unknown> | null
  fallback: string
}) => toStructuredErrorMessage(data) || raw || fallback

export const normalizeSetupStoreIdentity = (value: unknown): SetupStatus['storeIdentity'] => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  const raw = value as Record<string, unknown>
  const storeName = typeof raw.storeName === 'string' ? raw.storeName.trim() : ''
  const primaryDomain = typeof raw.primaryDomain === 'string' ? raw.primaryDomain.trim() : ''
  const timezone = typeof raw.timezone === 'string' ? raw.timezone.trim() : ''
  const currency = typeof raw.currency === 'string' ? raw.currency.trim() : ''
  if (!storeName && !primaryDomain && !timezone && !currency) return null
  return {
    storeName,
    ...(primaryDomain ? { primaryDomain } : {}),
    timezone,
    currency,
  }
}

export const normalizeSetupOnboardingPreferences = (value: unknown): SetupStatus['onboardingPreferences'] => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  const raw = value as Record<string, unknown>
  const paymentStrategy = typeof raw.paymentStrategy === 'string' ? raw.paymentStrategy.trim() : ''
  const emailProvider = typeof raw.emailProvider === 'string' ? raw.emailProvider.trim() : ''
  const botProtection = typeof raw.botProtection === 'string' ? raw.botProtection.trim() : ''
  if (!paymentStrategy && !emailProvider && !botProtection) return null

  return {
    paymentStrategy:
      paymentStrategy === 'stripe' || paymentStrategy === 'bank_transfer' ? paymentStrategy : 'configure_later',
    emailProvider:
      emailProvider === 'gmail' || emailProvider === 'outlook' || emailProvider === 'custom_smtp'
        ? emailProvider
        : 'configure_later',
    botProtection:
      botProtection === 'turnstile' || botProtection === 'recaptcha' ? botProtection : 'configure_later',
  }
}

export const normalizeValidationSummary = (value: unknown): SetupValidationSummary | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  const raw = value as Record<string, unknown>
  const statusRaw = typeof raw.status === 'string' ? raw.status : 'warning'
  const status = statusRaw === 'ok' || statusRaw === 'warning' || statusRaw === 'error' ? statusRaw : 'warning'
  const checks = Array.isArray(raw.checks)
    ? raw.checks
        .map((entry) => {
          if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return null
          const check = entry as Record<string, unknown>
          const id = typeof check.id === 'string' ? check.id : ''
          const label = typeof check.label === 'string' ? check.label : id
          const statusValue = typeof check.status === 'string' ? check.status : 'warning'
          const message = typeof check.message === 'string' ? check.message : ''
          if (!id) return null
          const normalizedStatus: SetupValidationCheckStatus =
            statusValue === 'ok' ||
            statusValue === 'warning' ||
            statusValue === 'error' ||
            statusValue === 'skipped'
              ? statusValue
              : 'warning'
          return {
            id,
            label,
            status: normalizedStatus,
            message,
          }
        })
        .filter((entry): entry is NonNullable<typeof entry> => Boolean(entry))
    : []

  return {
    status,
    checks,
  }
}

export const toSetupStatus = ({
  data,
  defaults,
}: {
  data: Record<string, unknown> | null
  defaults: {
    firstInstallCompleted: boolean
    adminCount: number
    hasSuperAdmin: boolean
    hasSiteManager: boolean
  }
}): SetupStatus => ({
  firstInstallCompleted: Boolean(data?.firstInstallCompleted ?? defaults.firstInstallCompleted),
  adminCount: Number(data?.adminCount ?? defaults.adminCount),
  hasSuperAdmin: Boolean(data?.hasSuperAdmin ?? defaults.hasSuperAdmin),
  hasSiteManager: Boolean(data?.hasSiteManager ?? defaults.hasSiteManager),
  installedAt: (data?.installedAt as string | null | undefined) ?? null,
  configVersion: data?.configVersion as number | undefined,
  bootstrapState: data?.bootstrapState as SetupStatus['bootstrapState'],
  storeIdentity: normalizeSetupStoreIdentity(data?.storeIdentity),
  onboardingPreferences: normalizeSetupOnboardingPreferences(data?.onboardingPreferences),
})

export const toSetupCompletionResult = ({
  data,
}: {
  data: Record<string, unknown> | null
}): SetupCompletionResult => ({
  status: toSetupStatus({
    data,
    defaults: {
      firstInstallCompleted: true,
      adminCount: 1,
      hasSuperAdmin: true,
      hasSiteManager: false,
    },
  }),
  recovery:
    data?.recovery && typeof data.recovery === 'object'
      ? {
          identifier:
            typeof (data.recovery as { identifier?: unknown }).identifier === 'string'
              ? (data.recovery as { identifier: string }).identifier
              : null,
          generatedAt:
            typeof (data.recovery as { generatedAt?: unknown }).generatedAt === 'string'
              ? (data.recovery as { generatedAt: string }).generatedAt
              : null,
        }
      : null,
  validation: normalizeValidationSummary(data?.validation),
})
